import React, { ComponentProps, useEffect, useState } from 'react';

import { Table } from '@qality/common-components/src/atoms/Table';
import { SortMode } from '@qality/common-model/src/sorting/Sort';

import { WrapperStyled } from './SortableHeader.styled';
import { SortingIcon } from './SortingIcon';

type Props<SortStrategy extends string> = {
  children: React.ReactNode;
  sortStrategies: Record<SortMode, SortStrategy>;
  sortStrategy: SortStrategy;
  onSortStrategyChange: (s: SortStrategy) => void;
} & Omit<ComponentProps<typeof Table.ClickableTh>, 'ref'>;

const SortOrder: Array<SortMode> = ['none', 'asc', 'dsc'];
const InitialSortIndex = 0;

export default function SortableHeader<SortStrategy extends string>({
  children,
  sortStrategy,
  onSortStrategyChange,
  sortStrategies,
  ...otherProps
}: Props<SortStrategy>): JSX.Element {
  const [sortIndex, setSortIndex] = useState(InitialSortIndex);

  useEffect(() => {
    resetSortIndexIfNeeded();
  }, [sortStrategy]);

  const resetSortIndexIfNeeded = () => {
    if (isCurrentSortStrategyForOtherHeader()) {
      setSortIndex(InitialSortIndex);
    }
  };

  const isCurrentSortStrategyForOtherHeader = () => {
    return ![sortStrategies.asc, sortStrategies.dsc].includes(sortStrategy);
  };

  const getNextSortIndexAndStrategy = () => {
    const newSortIndex = (sortIndex + 1) % SortOrder.length;
    const newSortKey = SortOrder[newSortIndex];
    const newSortStrategy = sortStrategies[newSortKey];

    return { newSortIndex, newSortStrategy };
  };

  const handleClick = () => {
    const { newSortIndex, newSortStrategy } = getNextSortIndexAndStrategy();

    setSortIndex(newSortIndex);
    onSortStrategyChange(newSortStrategy);
  };

  return (
    <Table.ClickableTh
      onClick={handleClick}
      data-testid='sortable-header'
      {...otherProps}
    >
      <WrapperStyled>
        {children}
        <SortingIcon sortMode={SortOrder[sortIndex]} />
      </WrapperStyled>
    </Table.ClickableTh>
  );
}
